import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Select } from 'antd';
import styled from '@emotion/styled';
import PricingCard from './PricingCard';
import EditStrategyPopup from './EditStrategyPopup';
import PopUpVideoLink from '../../common/PopUpVideoLink/PopUpVideoLink';
import { text2Medium } from '../../../constants/classes';
import { theme } from '../../../constants';

const { Option } = Select;

const TopRow = styled.div`
  display: flex;
  ${''};
`;
const BottomRow = styled.div`
  margin-top: 1.5em;
  display: flex;
  justify-content: space-evenly;
`;

const SelectCol = styled.div`
  width: 29%;
  display: flex;
  flex-direction: column;
  &:not(:first-of-type) {
    margin-left: 18px;
  }
  &:not(:last-child) {
    margin-right: 18px;
  }
  &:last-child {
    margin-right: 5px;
  }
`;

const To = styled.span`
  font-size: 16px;
  position: relative;
  top: 0.5em;
`;

@injectIntl
@inject('enumsStore')
@inject('toastsStore')
@inject('settingsStore')
@observer
class Pricing extends Component {
  constructor(props) {
    super();
    this.state = {
      selectedDestId: props.settingsStore.markets.destinations[0]
        ? props.settingsStore.markets.destinations[0].id
        : null,
      selectedSrcId: props.settingsStore.markets.sources[0]
        ? props.settingsStore.markets.sources[0].id
        : null,
      selectedAccountCode: props.settingsStore.markets.defaultSellersAccount.id,
      strategyIdToEdit: null,
    };
    this.breadcrumb = {
      name: props.intl.formatMessage({ id: 'settingsModal.pricing' }),
      clickHandler: this.closeEditPopup,
    };
  }

  componentDidMount() {
    this.props.updateBreadcrumbs(this.breadcrumb, true);
    this.handleDestChange(this.state.selectedDestId);
  }

  getStrategyDefaultValues = type => {
    const { selectedSrcId,selectedDestId, selectedAccountCode } = this.state;
    return this.props.settingsStore.pricing.fetchStrategyDefaultValues(
      type,
      selectedSrcId,
      selectedDestId,
      selectedAccountCode,
    );
  };

  handleSrcChange = id => {
    this.setState({ selectedSrcId: id }, () => this.fetchPricingList());
  };

  handleAccountChange = code => {
    this.setState({ selectedAccountCode: code }, () => this.fetchPricingList());
  };

  fetchPricingList = () => {
    const { selectedSrcId: src, selectedAccountCode: account, selectedDestId: dest } = this.state;
    if (src && account) {
      this.props.settingsStore.pricing.fetchPricingList(src, dest, account);
    }
  };

  showEditPopup = id => {
    this.setState({
      isEdit: true,
      strategyIdToEdit: id,
    });
  };

  closeEditPopup = (shouldResetData = this.state.strategyIdToEdit === 'rangedProfit') => {
    const {
      updateBreadcrumbs,
      settingsStore: {
        pricing: { rangedPricingList },
      },
    } = this.props;
    updateBreadcrumbs(this.breadcrumb, true);
    if (shouldResetData) {
      rangedPricingList.forEach(pr => pr.resetValues());
    }
    this.setState({
      isEdit: false,
      strategyIdToEdit: null,
    });
  };

  saveEditPricing = pricingObj => {
    const { selectedSrcId, strategyIdToEdit, selectedAccountCode } = this.state;
    const {
      toastsStore,
      enumsStore,
      settingsStore: {
        pricing:{
          editPricing,
          isUpdatingAllStore,
          setIsUpdatingAllStore,
          updateListedItemsStrategy
        },
      }
    } = this.props;
    const newObj = {
      ...pricingObj,
      sourceMarketplaceId: selectedSrcId,
    };

    editPricing(newObj, strategyIdToEdit, selectedAccountCode)
    .then(() => {
      if(isUpdatingAllStore){
        const payload={
          sourceMarketplaceId: selectedSrcId,
          sellerAccountId: selectedAccountCode,
          strategyType: strategyIdToEdit,
        }
        payload[strategyIdToEdit]=pricingObj;
        updateListedItemsStrategy(payload)
        setIsUpdatingAllStore(!isUpdatingAllStore)
      }
      this.fetchPricingList();
      this.closeEditPopup();
      toastsStore.showToast({
        type: enumsStore.enums.TOAST_TYPES.INFO,
        message: 'Pricing saved successfully',
      });
    })
    .catch(err => {
      console.error(err);
      toastsStore.showToast({
        type: enumsStore.enums.TOAST_TYPES.WARNING,
        message: 'There was an trying to save the pricing',
      });
    });
  };

  saveRangedPricing = () => {
    const { selectedSrcId, selectedAccountCode } = this.state;
    const {
      toastsStore,
      enumsStore,
      settingsStore: {
        pricing:{
          editRangedPricing,
          isUpdatingAllStore,
          changedRangedPricingList,
          setIsUpdatingAllStore,
          updateListedItemsStrategy
        },
      }
    } = this.props;
    const newObj = {
      sourceMarketplaceId: selectedSrcId,
      sellerAccountId: selectedAccountCode,
    };

    editRangedPricing(newObj)
    .then(() => {
      if(isUpdatingAllStore){
        const payload={
          sourceMarketplaceId: selectedSrcId,
          sellerAccountId: selectedAccountCode,
          strategyType: 'ranged',
        }
        payload.ranged=changedRangedPricingList;
        setIsUpdatingAllStore(!isUpdatingAllStore)
        updateListedItemsStrategy(payload)
      }
      this.fetchPricingList();
      this.closeEditPopup(false);
      toastsStore.showToast({
        type: enumsStore.enums.TOAST_TYPES.INFO,
        message: 'Pricing saved successfully',
      });
    })
    .catch(err => {
      console.error(err);
      toastsStore.showToast({
        type: enumsStore.enums.TOAST_TYPES.WARNING,
        message: 'There was an trying to save the pricing',
      });
    });
  };

  handleDestChange = id => {
    this.props.settingsStore.markets.fetchSellersAccounts(id);
    this.setState({ selectedDestId: id }, () => this.fetchPricingList());
  };

  render() {
    const { updateBreadcrumbs, settingsStore } = this.props;
    const {
      pricing: { pricingList, rangedPricingList,isUpdatingAllStore,setIsUpdatingAllStore },
      markets,
    } = settingsStore;
    const { sources, destinations, sellersAccounts } = markets;
    const { strategyIdToEdit, selectedDestId, selectedSrcId, selectedAccountCode } = this.state;
    return (
      <div>
        {this.state.isEdit ? (
          <div>
            <EditStrategyPopup
              updateBreadcrumbs={updateBreadcrumbs}
              saveNewPricing={
                strategyIdToEdit === 'rangedProfit' ? this.saveRangedPricing : this.saveEditPricing
              }
              pricingToEdit={
                strategyIdToEdit === 'rangedProfit'
                  ? rangedPricingList
                  : pricingList[strategyIdToEdit]
              }
              sellersAccounts={sellersAccounts}
              closePopup={this.closeEditPopup}
              selectedAccountCode={this.state.selectedAccountCode}
              getStrategyDefaultValues={this.getStrategyDefaultValues}
              type={strategyIdToEdit}
              isUpdatingAllStore={isUpdatingAllStore}
              setIsUpdatingAllStore={setIsUpdatingAllStore}
            />
          </div>
        ) : (
          <div>
            <TopRow>
              <SelectCol>
                <span>
                  <FormattedMessage
                    id="common.sourceMarketplace"
                    defaultMessage="Source Marketplace"
                  />
                </span>
                <Select onChange={this.handleSrcChange} defaultValue={selectedSrcId}>
                  {sources.map(source => (
                    <Option key={source.name} value={source.id}>
                      {source.name}
                    </Option>
                  ))}
                </Select>
              </SelectCol>
              <To style={{ display: 'flex', alignItems: 'center' }}>TO</To>
              <SelectCol>
                <span>
                  <FormattedMessage
                    id="pricing.destinationMarketplace"
                    defaultMessage="Destination Marketplace"
                  />
                </span>
                <Select onChange={this.handleDestChange} defaultValue={selectedDestId}>
                  {destinations.map(destination => (
                    <Option key={destination.name} value={destination.id}>
                      {destination.name}
                    </Option>
                  ))}
                </Select>
              </SelectCol>
              <SelectCol>
                <span>
                  <FormattedMessage id="pricing.accounts" defaultMessage="Accounts" />
                </span>
                <Select onChange={this.handleAccountChange} defaultValue={selectedAccountCode}>
                  {sellersAccounts.map(account => (
                    <Option key={account.id} value={account.id}>
                      {account.name}
                    </Option>
                  ))}
                </Select>
              </SelectCol>
            </TopRow>
            <BottomRow>
              {/* {Object.keys(pricingList).map(key => ( */}
              <PricingCard
                // key={key}
                handleClick={() => this.showEditPopup('fixedProfit')}
                pricing={pricingList.fixedProfit}
                type="fixedProfit"
              />
              <PricingCard
                // key={key}
                handleClick={() => this.showEditPopup('rangedProfit')}
                pricing={rangedPricingList}
                type="rangedProfit"
              />
              <PricingCard
                // key={key}
                handleClick={() => this.showEditPopup('penetration')}
                pricing={pricingList.penetration}
                type="penetration"
              />
              {/* ))} */}
            </BottomRow>
            <BottomRow>
              <PopUpVideoLink
                message="How To Define Profit And Break Even:"
                videoUrl="https://vimeo.com/475865290/16e9b651da"
                extraCSS={`
                  margin-bottom: 20px;
                  text-decoration: underline;
                  &:hover {
                    text-decoration: underline;
                  }
                  ${text2Medium}
                  font-size: ${theme.fontSize.medium};
                `}
              />
              <PopUpVideoLink
                message="Ranged Profit Feature:"
                videoUrl="https://vimeo.com/506686040/37e97762f0"
                extraCSS={`
                  margin-bottom: 20px;
                  text-decoration: underline;
                  &:hover {
                    text-decoration: underline;
                  }
                  ${text2Medium}
                  font-size: ${theme.fontSize.medium};
                `}
              />
            </BottomRow>
          </div>
        )}
      </div>
    );
  }
}

Pricing.propTypes = {
  settingsStore: PropTypes.object,
  toastsStore: PropTypes.object,
  enumsStore: PropTypes.object,
  intl: PropTypes.object,
  updateBreadcrumbs: PropTypes.func,
};

export default Pricing;
